import { createReducer, on } from '@ngrx/store';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import { UserOrganization, UserProfile } from './user-profile.model';
import * as UserProfileActions from './user-profile.actions';
import * as authActions from '../../../authentication/store/auth/auth.actions';
import { Organization } from '../models/organization.model';
import { Language } from '../../languages/language.model';
import { OrgBusinessUnit } from '../../org-business-units';

export const USER_PROFILE_KEY = 'userProfile';

export interface State {
  userProfile?: UserProfile;
  queryParams?: QueryParams;
  profileConnection?: string | null;
  err?: Error;
  userOrganizations?: UserOrganization[];
  hasBusinessUnits: boolean;
  userBusinessUnits?: OrgBusinessUnit[];
  organizationLanguages?: Language[];
  organization?: Organization;
  ui?: {
    showProfileForm?: boolean;
    showPasswordForm?: boolean;
    isProfileSubmitting?: boolean;
    errResponse?: boolean;
  };
}

const initialState: State = {
  userProfile: null,
  queryParams: {
    skip: 0,
    take: 1,
    totalCount: 0,
    include: 'AuthConnection'
  },
  profileConnection: null,
  err: null,
  userOrganizations: null,
  hasBusinessUnits: false,
  userBusinessUnits: null,
  organizationLanguages: null,
  ui: { showProfileForm: false, errResponse: false, showPasswordForm: false }
};

export const reducer = createReducer(
  initialState,
  on(UserProfileActions.getUserProfileComplete, (state, action) => ({
    ...state,
    userProfile: action.result,
    err: null
  })),
  on(UserProfileActions.profileLogout, () => ({ ...initialState })),
  on(UserProfileActions.showProfileForm, (state, { showProfileForm }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showProfileForm
      }
    };
  }),
  on(UserProfileActions.showPasswordForm, (state, { showPasswordForm }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        showPasswordForm
      }
    };
  }),
  on(UserProfileActions.setConnection, (state, { profileConnection }) => {
    return {
      ...state,
      profileConnection,
      ui: {
        ...state.ui
      }
    };
  }),
  on(UserProfileActions.setIsProfileSubmitting, (state, { isProfileSubmitting }) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isProfileSubmitting
      }
    };
  }),
  on(UserProfileActions.setProfileError, (state, action) => {
    return {
      ...state,
      err: action.err,
      ui: {
        ...state.ui,
        errResponse: true
      }
    };
  }),
  on(UserProfileActions.getUserOrganizationsComplete, (state, action) => ({
    ...state,
    userOrganizations: action.userOrganizations,
    err: null
  })),
  on(UserProfileActions.getUserBusinessUnitsComplete, (state, action) => ({
    ...state,
    hasBusinessUnits: !!action.businessUnits,
    userBusinessUnits: action.businessUnits,
    err: null
  })),
  on(UserProfileActions.getUserOrganizationComplete, (state, action) => ({
    ...state,
    organization: action.userOrganization,
    err: null
  })),
  on(UserProfileActions.getUserOrganizationLanguagesComplete, (state, action) => {
    return {
      ...state,
      organizationLanguages: action.languages
    };
  })
);

export const getProfileQueryParams = (state: State) => state?.queryParams;

export const getSelectedUserProfile = (state: State) => state?.userProfile;

export const getSelectedUserProfileUID = (state: State) => state?.userProfile?.uId;

export const getShowProfileForm = (state: State) => state?.ui?.showProfileForm;

export const getShowPasswordForm = (state: State) => state?.ui?.showPasswordForm;

export const getIsProfileSubmitting = (state: State) => state?.ui?.isProfileSubmitting;

export const getDistanceUnit = (state: State) => state?.userProfile?.culture.distanceUnit;

export const getTimezone = (state: State) => state?.userProfile?.culture.timezone;

export const getProfileConnection = (state: State) => state?.profileConnection;

export const getUserProfileOrganizations = (state: State) => state?.userOrganizations;

export const getOrganization = (state: State) => state?.organization;

export const getError = (state: State) => state?.err?.message;

export const getUserProfileOrganizationLanguages = (state: State) => state?.organizationLanguages;

export const getUserHasLanguages = (state: State) => !!state?.organizationLanguages && state?.organizationLanguages.length > 1;

export const getHasBusinessUnits = (state: State) => state?.hasBusinessUnits;

export const getUserBusinessUnitsList = (state: State) => state?.userBusinessUnits;
